// Interactive Test Runner & Verification modal for Outbound HTTP APIs (WP-13).
import { useState } from 'react';
import { useI18n } from '../../lib/i18n';
import Modal from '../ui/Modal';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import {
  verifyHttpApi,
  type HttpApiDoc,
  type VerifyHttpApiResult,
} from '../../lib/httpApi';

interface HttpApiTestModalProps {
  open: boolean;
  onClose: () => void;
  api: HttpApiDoc | null;
  onVerified?: () => void;
}

export function HttpApiTestModal({ open, onClose, api, onVerified }: HttpApiTestModalProps) {
  const { t } = useI18n();

  const [subscriberName, setSubscriberName] = useState('Ahmed Al-Busaidi');
  const [subscriberPhone, setSubscriberPhone] = useState('+96891234567');
  const [customVariablesJson, setCustomVariablesJson] = useState('{\n  "order_id": "10042"\n}');

  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<VerifyHttpApiResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  if (!api) return null;

  const handleRunTest = async () => {
    setTesting(true);
    setResult(null);
    setTestError(null);

    let parsedCustomVars: Record<string, any> = {};
    if (customVariablesJson.trim()) {
      try {
        parsedCustomVars = JSON.parse(customVariablesJson.trim());
      } catch (err: any) {
        setTestError(t('httpApi.test.invalidCustomJson') + ': ' + err.message);
        setTesting(false);
        return;
      }
    }

    const sampleContext = {
      subscriber: {
        name: subscriberName.trim(),
        phone: subscriberPhone.trim(),
      },
      variables: parsedCustomVars,
    };

    try {
      const res = await verifyHttpApi(api.id, sampleContext);
      setResult(res);
      if (res.ok && onVerified) {
        onVerified();
      }
    } catch (err: any) {
      setTestError(err.message || t('httpApi.test.executionFailed'));
    } finally {
      setTesting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('httpApi.test.modalTitle') + ': ' + api.name}
    >
      <div className="space-y-4 max-h-[80vh] overflow-y-auto pe-1">
        {/* API Endpoint Summary */}
        <div className="rounded-lg bg-slate-50 p-3 border border-slate-200 dark:bg-slate-900/60 dark:border-slate-800 flex items-center gap-2">
          <Badge variant="brand">{api.method}</Badge>
          <code className="text-xs font-mono text-slate-700 dark:text-slate-300 break-all flex-1">
            {api.url}
          </code>
        </div>

        {/* Sample Context Input Section */}
        <div className="space-y-3 border-t border-slate-200 pt-3 dark:border-slate-800">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">
            {t('httpApi.test.sampleContextTitle')}
          </h4>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="block text-[11px] font-medium text-slate-500 mb-1">
                {t('httpApi.test.subscriberName')}
              </label>
              <input
                type="text"
                value={subscriberName}
                onChange={(e) => setSubscriberName(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-900"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-slate-500 mb-1">
                {t('httpApi.test.subscriberPhone')}
              </label>
              <input
                type="text"
                value={subscriberPhone}
                onChange={(e) => setSubscriberPhone(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-900 font-mono"
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-medium text-slate-500 mb-1">
              {t('httpApi.test.customVariables')}
            </label>
            <textarea
              rows={3}
              value={customVariablesJson}
              onChange={(e) => setCustomVariablesJson(e.target.value)}
              className="w-full font-mono text-xs rounded-lg border border-slate-200 bg-white p-2.5 outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-900"
            />
          </div>

          <div className="flex justify-end">
            <Button
              variant="primary"
              type="button"
              onClick={handleRunTest}
              disabled={testing}
            >
              {testing ? t('httpApi.test.running') : t('httpApi.test.runTestButton')}
            </Button>
          </div>
        </div>

        {/* Test Error Banner */}
        {testError && (
          <div className="rounded-lg bg-rose-50 p-3 text-xs text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">
            <strong>{t('httpApi.test.error')}:</strong> {testError}
          </div>
        )}

        {/* Results Panel */}
        {result && (
          <div className="border-t border-slate-200 pt-4 space-y-3 dark:border-slate-800">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                {t('httpApi.test.resultsTitle')}
              </h4>
              <div className="flex items-center gap-2">
                <Badge variant={result.ok ? 'success' : 'danger'}>
                  {result.status ? `${result.status} ${result.ok ? 'OK' : 'Error'}` : 'Failed'}
                </Badge>
                <span className="text-xs font-mono text-slate-500">
                  {result.durationMs} ms
                </span>
              </div>
            </div>

            {/* Extracted Variables Table */}
            <div>
              <label className="block text-[11px] font-medium text-slate-500 mb-1">
                {t('httpApi.test.extractedVariables')}
              </label>
              {Object.keys(result.variables || {}).length === 0 ? (
                <p className="text-xs text-slate-400 italic bg-slate-50 p-2.5 rounded-lg dark:bg-slate-900/40">
                  {t('httpApi.test.noVariablesExtracted')}
                </p>
              ) : (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5 space-y-1.5 dark:border-slate-800 dark:bg-slate-900/60">
                  {Object.entries(result.variables).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between text-xs">
                      <code className="text-primary font-bold font-mono">
                        {'{' + k + '}'}
                      </code>
                      <span className="text-slate-700 dark:text-slate-300 font-mono bg-white dark:bg-slate-800 px-2 py-0.5 rounded border border-slate-200 dark:border-slate-700">
                        {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Response Payload */}
            <div>
              <label className="block text-[11px] font-medium text-slate-500 mb-1">
                {t('httpApi.test.responsePayload')}
              </label>
              <pre className="max-h-56 overflow-auto rounded-lg bg-slate-950 p-3 font-mono text-xs text-emerald-400">
                {typeof result.data === 'string'
                  ? result.data
                  : JSON.stringify(result.data, null, 2)}
              </pre>
            </div>
          </div>
        )}

        <div className="flex justify-end pt-3 border-t border-slate-200 dark:border-slate-800">
          <Button variant="ghost" type="button" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
